/**
 * Recorded speech: which take belongs to which line, language and tone.
 *
 * Everything else this game makes a noise with is synthesised - core/audio
 * produces samples and nothing is loaded from disk. A voice is the exception,
 * because a person saying a line is not something you can synthesise, and the
 * moment the game speaks it has to speak the player's language.
 *
 * **A voice is never the line.** The written line is still shown, exactly as
 * before; a take plays over the top of it when one exists. So a language or a
 * tone with no recording is silent and complete, not broken - which is the
 * only honest way to ship a voice for one language before the other, and the
 * reason none of this can leave the game half-spoken.
 *
 * Falling back works the way `say` works: an adult request with no adult take
 * uses the clean take **in the same language**, and never crosses languages.
 * A Ukrainian voice under an English line would be worse than silence.
 *
 * Pure: a table of names. Loading and playing belong to platform/.
 */

import type { LineKey } from "../text/tone.js";
import type { Tone } from "../text/tone.js";
import type { Lang } from "../text/lang.js";

/** Where the takes are served from, relative to the page. */
export const VOICE_ROOT = "voice/";

export interface VoiceTake {
  readonly line: LineKey;
  readonly lang: Lang;
  /**
   * Which tone this take is for.
   *
   * A "family" take is used for both tones when there is no adult one; an
   * "adult" take is used only when the 18+ switch is on. That asymmetry is
   * deliberate and is the same one the written lines have: the failure mode
   * of a missing take is a clean line heard by an adult, never the reverse.
   */
  readonly tone: Tone;
  /** File name under VOICE_ROOT/<lang>/. */
  readonly file: string;
}

/**
 * Every take that exists.
 *
 * Two so far, and they are the same moment in both languages: the villager
 * you just hit, with the 18+ switch on. Everything else in this game is still
 * text and a synthesised noise.
 *
 * Neither has a clean counterpart, and that is the right way round - a player
 * who asked for the kind game hears nothing rather than something they asked
 * not to hear.
 */
export const VOICE: readonly VoiceTake[] = [
  {
    line: "villager-hit",
    lang: "uk",
    tone: "adult",
    file: "villager-hit.adult.mp3",
  },
  {
    line: "villager-hit",
    lang: "en",
    tone: "adult",
    file: "villager-hit.adult.mp3",
  },
];

/**
 * The take to play, or undefined when the game should just stay quiet.
 *
 * The table is a parameter so the fallback rules can be driven with takes
 * that do not exist yet - there is one recording today, and a rule about what
 * happens when there are two cannot be tested against a table holding one.
 */
export function voiceFor(
  line: LineKey, lang: Lang, tone: Tone,
  from: readonly VoiceTake[] = VOICE,
): VoiceTake | undefined {
  const inLanguage = from.filter((v) => v.line === line && v.lang === lang);
  if (tone === "adult") {
    const blunt = inLanguage.find((v) => v.tone === "adult");
    if (blunt !== undefined) return blunt;
  }
  return inLanguage.find((v) => v.tone === "family");
}

/** Where a take is fetched from. */
export function voiceUrl(take: VoiceTake): string {
  return `${VOICE_ROOT}${take.lang}/${take.file}`;
}
